import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { CliLinkData, Token, JourneyFlowType } from './token';
import { SessionStorageService, LocalStorageService } from 'ngx-webstorage';
import { Application, MerchantConfig } from '../application/application';
import { ChatCustomerDetails, PersonalDetailsSession } from '../customer/personal-details/contracts/personal-details';
import { EmploymentDetailsSession } from '../customer/employment-details/contracts/employment';
import { EmploymentDetailsSession as EmploymentDetailsSessionv2 } from '../customer/employment-details-v2/contracts/employment';
import { LoaderService, Loader } from '../common/loader/loader.service';
import { GlobalParams } from '../common/tracking/aws-tracker';
import { KycFeaturePage } from 'app/customer/documents/contracts/document';
import { MoratoriumFeature } from 'app/features/moratorium-optout/moratorium';
import { Channel } from 'app/channel.service';

declare var $: any;
declare var window: any;
// declare var mixpanel: any;



function e(p) {
  let d: any;
  const conversion = JSON.stringify(p);
  d = btoa(conversion).split('').reverse();
  d.push(d.shift());
  return d;
}

function d(s) {
  s.unshift(s.pop());
  let value;
  value = JSON.parse(atob(s.reverse().join('')));
  return value;
}


interface JwtToken {
  exp: number;
  sub: string;
  access_token: string;
}

interface CacheStorageWithExpiry {
  data: any;
  expiry: number;
}
@Injectable()
export class TokenService {

  private jwtHelper: JwtHelperService = new JwtHelperService();
  tokenInMemory: JwtToken = { exp : 0, sub : '', access_token : ''};

  constructor(private router: Router, private loaderService: LoaderService, private sessionStorage: SessionStorageService, private localStorage: LocalStorageService) { }
  public setToken(token) {
    this.sessionStorage.store('zest', token);
  }

  public getToken() {
    return this.sessionStorage.retrieve('zest') as Token;
  }

  checkforTokenValidity(tokenExpiry: number) {
    const currTime = new Date().getTime() / 1000;
    if (currTime > tokenExpiry) {
      this.logout();
    }
  }

  getSessionToken() {
    const token = this.getToken();
    if (this.tokenInMemory.access_token) {
      this.checkforTokenValidity(this.tokenInMemory.exp);
      return this.tokenInMemory.access_token;
    } else if (token) {
      const decodedToken: JwtToken = this.jwtHelper.decodeToken(token.access_token);
      this.checkforTokenValidity(decodedToken.exp);
      this.tokenInMemory = decodedToken;
      this.tokenInMemory.access_token = token.access_token;
      return token.access_token;
    } else {
      this.logout();
    }
  }

  public getLocalToken() {
    const token = localStorage.getItem('zest-token');
    return token ? token : null;
  }
  copyTokenFromLocalToSession() {
    if (this.getLocalToken()) {
      const token = new Token(this.getLocalToken());
      this.saveToSession(token);
    }
  }

  setUTSEncryptedTokenCache(token: string) {
    this.sessionStorage.store(`zest-uts-token-${token}`, true);
  }

  getUTSEncryptedTokenCache(token: string) {
    return this.sessionStorage.retrieve(`zest-uts-token-${token}`);
  }

  getAccessToken() {
    const token = this.sessionStorage.retrieve('zest') as Token;
    if (token) {
      return token.access_token;
    } else {
      return null;
    }
  }
  getTokenExpiryTime() {
    const token = this.sessionStorage.retrieve('zest') as Token;
    return token ? this.jwtHelper.decodeToken(token.access_token).exp as number : null;
  }
  getCustomerId() {
    const partnerToken = this.getPartnerRedirectionDetails() ? this.getPartnerRedirectionDetails() : null;
    if (partnerToken && (this.jwtHelper.decodeToken(partnerToken.token).client_id === partnerToken.merchantId)) {
      return partnerToken.customerId;
    } else {
      if (this.tokenInMemory.sub) {
        this.checkforTokenValidity(this.tokenInMemory.exp);
        return this.tokenInMemory.sub;
      }
      const token = this.getToken();
      if (token) {
        const tokenExp = this.getTokenExpiryTime();
        this.checkforTokenValidity(tokenExp);
        const decodedToken: JwtToken = this.jwtHelper.decodeToken(this.getSessionToken());
        return decodedToken.sub;
      }
      return null;
    }
  }
  getWithExpiry = (key: string) => {
    try {
      const itemStr = window.localStorage.getItem(key);
      if (!itemStr) {
        return null;
      }
      const item: CacheStorageWithExpiry = JSON.parse(itemStr);
      const expiry = item.expiry;
      const now = new Date();
      if (now.getTime() > expiry || !item.data) {
        window.localStorage.removeItem(key);
        return null;
      }
      return d(item.data);
    } catch {
      return null;
    }
  }
  setWithExpiry = (key: string, data: any, ttl: number = 7200000) => {
    try {
      const now = new Date();
      const expiry = now.getTime() + ttl;
      window.localStorage.setItem(key, JSON.stringify({ data: e(data), expiry }));
    } catch {
      return null;
    }
  }
  getCustomerDetails(): ChatCustomerDetails {
    return this.sessionStorage.retrieve('zest-customer') as ChatCustomerDetails;
  }
  setCustomerDetails(ChatCustomerDetails: ChatCustomerDetails) {
    return this.sessionStorage.store('zest-customer', ChatCustomerDetails);
  }
  setPDSession(pDetails: PersonalDetailsSession) {
    return this.localStorage.store('zest-pdetails', e(pDetails));
  }
  getPDSession() {
    const ses = this.localStorage.retrieve('zest-pdetails');
    if (ses) {
      try {
        const j = d(ses);
        return j.CustomerId === this.getCustomerId() ? j : undefined;
      } catch (error) {
        this.localStorage.store('zest-pdetails', null);
      }
    }
    return undefined;
  }
  setEDSession(eDetails: EmploymentDetailsSession | EmploymentDetailsSessionv2) {
    return this.localStorage.store('zest-edetails', e(eDetails));
  }
  recieveDatafromPartner(data: any) {
    for (const key of Object.keys(data)) {
      this.updateAndSetGlobalParams(key, data[key]);
    }
    return this.sessionStorage.store('zest-widget-details', data);
  }
  getDatafromPartnerSession() {
    return this.sessionStorage.retrieve('zest-widget-details');
  }
  removeDatafromPartnerSession() {
    return this.sessionStorage.clear('zest-widget-details');
  }
  getEDSession() {
    const ses = this.localStorage.retrieve('zest-edetails');
    if (ses) {
      try {
        const j = d(ses);
        return j.CustomerId === this.getCustomerId() ? j : undefined;
      } catch (error) {
        this.localStorage.store('zest-edetails', null);
      }
    }
    return undefined;
  }
  saveToSession(token: any) {
    if (window.BroadcastChannel) {
    const channel = Channel.getChannelObj();
    channel.postMessage('Authentication', this.getCustomerContact());
    }
    this.sessionStorage.store('zest', token);
  }
  saveAadhaarNumber(aadhaarNumber: string) {
    this.sessionStorage.store('aadhaarNumber', aadhaarNumber);
  }
  getAadhaarNumber(): string {
    return this.sessionStorage.retrieve('aadhaarNumber') as string;
  }
  setAffiliateCampaign(affiliate: string) {
    this.sessionStorage.store('affiliate', affiliate);
  }
  setShieldData(data: any) {
    this.sessionStorage.store('shield-intelligence', data);
  }
  getShieldData() {
    return this.sessionStorage.retrieve('shield-intelligence') as any;
  }
  getAffiliateCampaign(): string {
    return this.sessionStorage.retrieve('affiliate') as string;
  }
  setDocumentReferrer(referrer: string) {
    this.sessionStorage.store('referrer', referrer);
  }
  getDocumentReferrer(): string {
    return this.sessionStorage.retrieve('referrer') as string;
  }
  setAadhaarFailureCount(aadhaarFailureCount: number) {
    this.sessionStorage.store('aadhaarFailureCount', aadhaarFailureCount);
  }
  getAadhaarFailureCount(): number {
    return this.sessionStorage.retrieve('aadhaarFailureCount') as number;
  }
  storeAadhaarErrorMsg(msg: string) {
    this.sessionStorage.store('zest-aadhaar-error-msg', msg);
  }
  getAadhaarErrorMsg() {
    return this.sessionStorage.retrieve('zest-aadhaar-error-msg');
  }
  setIfscValidationFailureCount(aadhaarFailureCount: number) {
    this.sessionStorage.store('ifscValidationFailureCount', aadhaarFailureCount);
  }
  getIfscValidationFailureCount(): number {
    return this.sessionStorage.retrieve('ifscValidationFailureCount') as number;
  }
  removeAllToasts() {
    $('#toast-container').remove();
    $('.toast').remove();
  }
  removeAllModals() {
    $('.modal.open').modal('close');
  }
  removeAll(removeLocal = true) {
    this.sessionStorage.clear();
    this.tokenInMemory = { exp : 0, sub : '', access_token : ''};
    if (removeLocal) {
      localStorage.removeItem('zest-token');
    }
  }
  removeAllExceptToken() {
    const token: Token = this.getToken();
    this.removeAll();
    if (token) {
      this.saveToSession(token);
    }
  }
  removeOnlyToken() {
    localStorage.removeItem('zest-token');
    this.tokenInMemory = { exp : 0, sub : '', access_token : ''};
    this.sessionStorage.clear('zest');
    return true;
  }
  logout(removeLocal = true) {
    // const moengage = window.Moengage;
    this.removeAll(removeLocal);
    this.loaderService.ShowLoaderSource.next(new Loader(false, ''));
    this.removeAllModals();
    this.removeAllToasts();
    this.tokenInMemory = { exp : 0, sub : '', access_token : ''};
    // if (window.mixpanel) {
    //  mixpanel.reset();
    // }
    // if (moengage) {
    //  moengage.destroy_session();
    // }
    this.router.navigate(['/authentication/logout']);
  }
  cancel(application: Application) {
    this.removeAll();
    this.removeAllModals();
    this.removeAllToasts();
    if (application.MerchantName.toLowerCase() === 'zefo') {
      window.location.href = 'https://www.gozefo.com/checkout';
    } else {
      window.location.href = application.ReturnUrl;
    }
  }
  getParamString() {
    const token = this.sessionStorage.retrieve('zest') as Token;
    const params: URLSearchParams = new URLSearchParams();
    for (const key in token) {
      if (token.hasOwnProperty(key)) {
        const val = token[key];
        params.set(key, val);
      }
    }
    return params.toString();
  }
  getReferMerchantKey() {
    return this.sessionStorage.retrieve('zest-merchant') ? this.sessionStorage.retrieve('zest-merchant') as string : null;
  }
  setReferMerchantKey(referMerchantKey: string) {
    this.sessionStorage.store('zest-merchant', referMerchantKey);
  }
  removeReferMerchantKey() {
    this.sessionStorage.clear('zest-merchant');
  }
  getMerchantCustomerId() {
    return this.sessionStorage.retrieve('zest-merchant-customer') ? this.sessionStorage.retrieve('zest-merchant-customer') as string : null;
  }
  setMerchantCustomerId(merchantCustomerId: string) {
    this.sessionStorage.store('zest-merchant-customer', merchantCustomerId);
  }
  getMerchantCustomerRedirectUrl() {
    return this.sessionStorage.retrieve('zest-merchant-customer-redirect-url') ? this.sessionStorage.retrieve('zest-merchant-customer-redirect-url') as string : null;
  }
  setMerchantCustomerRedirectUrl(merchantCustomerRedirectUrl: string) {
    this.sessionStorage.store('zest-merchant-customer-redirect-url', merchantCustomerRedirectUrl);
  }
  setEnachAttemptCount(count: number) {
    this.sessionStorage.store('zest-enach', count);
  }
  getEnachAttemptCount() {
    return this.sessionStorage.retrieve('zest-enach') as number;
  }
  removeEnachAttemptCount() {
    return this.sessionStorage.clear('zest-enach');
  }
  saveMerchantConfig(config: MerchantConfig) {
    this.sessionStorage.store('zest-mconfig', config);
  }
  getMerchantConfig() {
    return this.sessionStorage.retrieve('zest-mconfig') ? this.sessionStorage.retrieve('zest-mconfig') as MerchantConfig : null;
  }
  saveVoucherGenerationInProgress(status: boolean) {
    this.sessionStorage.store('zest-voucher-generation-txn', status);
  }
  getVoucherGenerationStatus() {
    return this.sessionStorage.retrieve('zest-voucher-generation-txn') ? this.sessionStorage.retrieve('zest-voucher-generation-txn') as boolean : null;
  }
  setVoucherGenFailed() {
    this.sessionStorage.store('zest-voucher-generation-failed', true);
  }
  getVoucherGenFailed() {
    return this.sessionStorage.retrieve('zest-voucher-generation-failed') as boolean;
  }
  getPerfiosFlag() {
    const perfiosFlag = this.sessionStorage.retrieve('zest-required-perfios');
    return (perfiosFlag !== null || perfiosFlag !== undefined) ? perfiosFlag as boolean : null;
  }
  setPerfiosFlag(status: boolean) {
    this.sessionStorage.store('zest-required-perfios', status);
  }
  saveRiskStatusCode(code: string) {
    this.sessionStorage.store('zest-error', code);
  }
  getRiskStatusCode() {
    return this.sessionStorage.retrieve('zest-error') ? this.sessionStorage.retrieve('zest-error') as string : null;
  }
  savePdAttemptLeft(attempts: any) {
    return this.sessionStorage.store('zest-pd-attempt', attempts);
  }
  getPdAttemptLeft() {
    return this.sessionStorage.retrieve('zest-pd-attempt') ? this.sessionStorage.retrieve('zest-pd-attempt') : null;
  }
  getGlobalParams() {
    return this.sessionStorage.retrieve('zest-params') ? this.sessionStorage.retrieve('zest-params') as GlobalParams : null;
  }
  updateAndSetGlobalParams(type: string, value: string) {
    let globalParams = this.getGlobalParams();
    if (!globalParams) {
      globalParams = new GlobalParams();
    }
    globalParams[type] = value;
    this.sessionStorage.store('zest-params', globalParams);
  }
  clearMerchantData() {
    const dataArray = ['zest-customer', 'zest-mconfig', 'zest-merchant', 'zest-contact'];
    dataArray.forEach(key => {
      this.sessionStorage.clear(key);
    });
  }
  setRedirectToSiFeaturePage(value: boolean) {
    this.sessionStorage.store('zest-setup-si-feature', value);
  }
  getRedirectToSiFeaturePage(): boolean {
    return this.sessionStorage.retrieve('zest-setup-si-feature') ? this.sessionStorage.retrieve('zest-setup-si-feature') : false;
  }
  setRedirectToRepaymentsFeaturePage(value: boolean) {
    this.sessionStorage.store('zest-setup-repayments-feature', value);
  }
  getRedirectToRepaymentsFeaturePage(): boolean {
    return this.sessionStorage.retrieve('zest-setup-repayments-feature') ? this.sessionStorage.retrieve('zest-setup-repayments-feature') : false;
  }
  setRedirectToBoltPage(value: boolean) {
    this.sessionStorage.store('zest-bolt-customer', value);
  }
  getRedirectToBoltPage(): boolean {
    return this.sessionStorage.retrieve('zest-bolt-customer') ? this.sessionStorage.retrieve('zest-bolt-customer') : false;
  }
  setRepaymentsSetupFromMyAccounts(value: boolean) {
    this.sessionStorage.store('zest-myaccounts-repayments-setup', value);
  }
  getRepaymentsSetupFromMyAccounts(): boolean {
    return this.sessionStorage.retrieve('zest-myaccounts-repayments-setup') ? this.sessionStorage.retrieve('zest-myaccounts-repayments-setup') : false;
  }
  setKycSkip(data: Object) {
    this.sessionStorage.store('zest-kyc-route', data);
  }
  getKycSkip(): any {
    return this.sessionStorage.retrieve('zest-kyc-route') ? this.sessionStorage.retrieve('zest-kyc-route') : null;
  }
  setBDFromMyAccounts(value: boolean) {
    this.sessionStorage.store('zest-myaccounts-bank-details-setup', value);
  }
  getBDFromMyAccounts(): boolean {
    return this.sessionStorage.retrieve('zest-myaccounts-bank-details-setup') ? this.sessionStorage.retrieve('zest-myaccounts-bank-details-setup') : false;
  }
  setKYCVerificationFromMyAccounts(value: boolean) {
    this.sessionStorage.store('zest-myaccounts-kyc-verification-setup', value);
  }
  getKYCVerificationFromMyAccounts(): boolean {
    return this.sessionStorage.retrieve('zest-myaccounts-kyc-verification-setup') ? this.sessionStorage.retrieve('zest-myaccounts-kyc-verification-setup') : false;
  }
  setKycVerification(kyc: boolean, okyc: boolean, version = 'v1') {
    const data = new KycFeaturePage(kyc, okyc, version);
    this.sessionStorage.store('zest-kyc-verification-setup', data);
  }
  getKycVerification(): KycFeaturePage {
    return this.sessionStorage.retrieve('zest-kyc-verification-setup') ? this.sessionStorage.retrieve('zest-kyc-verification-setup') : null;
  }
  setMoratoriumOptoutFlag(optOut: boolean, optInUser: boolean) {
    const moratoriumStatus: MoratoriumFeature = { optOut, optInUser };
    this.sessionStorage.store('zest-moratorium-setup', moratoriumStatus);
  }
  getMoratoriumOptoutFlag(): MoratoriumFeature {
    return this.sessionStorage.retrieve('zest-moratorium-setup') ? this.sessionStorage.retrieve('zest-moratorium-setup') : null;
  }
  setIsSignUp(isSignUp: boolean) {
    this.sessionStorage.store('zest-sign-up', isSignUp);
  }
  getIsSignUp(): boolean {
    const isSignUp = this.sessionStorage.retrieve('zest-sign-up');
    return isSignUp ? true : false;
  }
  setHideGmail(hideGmail) {
    this.sessionStorage.store('zest-hide-gmail', hideGmail);
  }
  getHideGmail() {
    const hideGmail = this.sessionStorage.retrieve('zest-hide-gmail');
    return hideGmail ? true : false;
  }
  getPanInfoShown(): boolean {
    return this.localStorage.retrieve('zest-paninfossubmitted') === this.getCustomerId();
  }
  setPanInfoShown() {
    return this.localStorage.store('zest-paninfossubmitted', this.getCustomerId());
  }
  setCustomMerchantConfig(flag: boolean = false) {
    this.sessionStorage.store('zest-customMerchantConfig', flag);
  }
  getCustomMerchantConfig(): boolean {
    return this.sessionStorage.retrieve('zest-customMerchantConfig') ? this.sessionStorage.retrieve('zest-customMerchantConfig') : false;
  }
  setSkipDashboard(skip) {
    this.sessionStorage.store('zest-skip-dashboard', skip);
  }
  getSkipDashboard(): boolean {
    const skip = this.sessionStorage.retrieve('zest-skip-dashboard');
    return skip ? skip : false;
  }
  setOfflineGcAgent(value) {
    this.sessionStorage.store('zest-offline-gc-agent', value);
  }
  getOfflineGcAgent(): boolean {
    const value = this.sessionStorage.retrieve('zest-offline-gc-agent');
    return value ? value : false;
  }
  setDisableRouteFlag() {
    this.sessionStorage.store('zest-disable-route', true);
  }
  getDisableRouteFlag() {
    return this.sessionStorage.retrieve('zest-disable-route') ? this.sessionStorage.retrieve('zest-disable-route') : false;
  }
  setMerchantSettings(settings: any) {
    this.sessionStorage.store('zest-gc-settings', encodeURIComponent(JSON.stringify(settings)));
  }
  getMerchantSettings() {
    return this.sessionStorage.retrieve('zest-gc-settings') ? JSON.parse(decodeURIComponent(this.sessionStorage.retrieve('zest-gc-settings'))) : false;
  }
  setADSelfie(value = true) {
    this.sessionStorage.store('zest-selfie-ad', value);
  }
  getADSelfie() {
    return this.sessionStorage.retrieve('zest-selfie-ad') ? this.sessionStorage.retrieve('zest-selfie-ad') : false;
  }
  setKycVersion(version: string) {
    this.sessionStorage.store('zest-kyc-version', version);
  }
  getKycVersion() {
    return this.sessionStorage.retrieve('zest-kyc-version') ? this.sessionStorage.retrieve('zest-kyc-version') : false;
  }
  setKycVerificationEnabledFlag(enabledFlag: boolean) {
    this.sessionStorage.store('zest-kyc-verification-enabled', enabledFlag);
  }
  setPartnerRedirectDetails(details: any = {}) {
    this.sessionStorage.store('zest-partner-redirect-details', details);
    this.localStorage.store('zest-partner-redirect-details', details);
  }
  getPartnerRedirectionDetails() {
    return this.sessionStorage.retrieve('zest-partner-redirect-details') ? this.sessionStorage.retrieve('zest-partner-redirect-details') : null;
  }
  getKycVerificationEnabledFlag() {
    return this.sessionStorage.retrieve('zest-kyc-verification-enabled') ? this.sessionStorage.retrieve('zest-kyc-verification-enabled') : false;
  }
  setSelectedWhiteListMerchant(merchant: string) {
    this.sessionStorage.store('zest-whitelist-merchant', merchant);
  }
  getSelectedWhitelistMerchant() {
    return this.sessionStorage.retrieve('zest-whitelist-merchant') ? this.sessionStorage.retrieve('zest-whitelist-merchant') : null;
  }
  setCJMFlag(cjmEnabled) {
    this.sessionStorage.store('zest-cjm', cjmEnabled);
  }
  getCJMFlag() {
    const skip = this.sessionStorage.retrieve('zest-cjm');
    return skip ? skip : false;
  }
  setJourney(journey, encode= false) {
    this.sessionStorage.store('zest-journey', encode ? e(journey) : journey);
  }
  getJourney(decode= false) {
    const journey = decode ? d(this.sessionStorage.retrieve('zest-journey')) : this.sessionStorage.retrieve('zest-journey');
    return journey ? journey : null;
  }
  setOKYCValidationFlag(flag) {
    this.sessionStorage.store('zest-okyc-validation', flag);
  }
  getOKYCValidationFlag() {
    return this.sessionStorage.retrieve('zest-okyc-validation') ? this.sessionStorage.retrieve('zest-okyc-validation') : false;
  }
  setGooglePayEnableFlag() {
    this.sessionStorage.store('zest-custom-flow-enabled', true);
  }
  getGooglePayEnableFlag() {
    return this.sessionStorage.retrieve('zest-custom-flow-enabled') ? this.sessionStorage.retrieve('zest-custom-flow-enabled') : false;
  }
  setCrossSellProduct(productId) {
    this.sessionStorage.store('zest-cs-product', productId);
  }
  getCrossSellProduct() {
    const csProduct = this.sessionStorage.retrieve('zest-cs-product');
    return csProduct ? csProduct : null;
  }
  // setMixPanelAlias() {
  //  const customerId = this.getCustomerId();
  //  if (window.mixpanel && this.getIsSignUp()) {
  //    mixpanel.alias(customerId);
  //    mixpanel.people.set_once({
  //      $mobile: `+91${this.getCustomerContact()}`
  //    });
  //    }
  //  this.setMixPanelUserId();
  // }
  // setMixPanelUserId() {
  // if (window.mixpanel) {
  //    const customerId = this.getCustomerId();
  //    const userDetails = this.getCustomerDetails();
  //    mixpanel.people.set({
  //      'USER_ID': customerId,
  //      $name: userDetails ? userDetails.name : ''
  //    });
  //    mixpanel.identify(customerId);
  //  }
  // }
  // resetMixPanel() {
  //  if (window.mixpanel) {
  //    mixpanel.reset();
  //  }
  // }
  setDoorStepEnableFlag(flag) {
    this.sessionStorage.store('zest-doorstep-feature', flag);
  }
  getDoorStepEnableFlag() {
    return this.sessionStorage.retrieve('zest-doorstep-feature') ? this.sessionStorage.retrieve('zest-doorstep-feature') : false;
  }
  setVideoKycEnableFlag(flag) {
    this.sessionStorage.store('zest-videokyc-feature', flag);
  }
  getVideoKycEnableFlag() {
    return this.sessionStorage.retrieve('zest-videokyc-feature') ? this.sessionStorage.retrieve('zest-videokyc-feature') : false;
  }
  setClPause(clPauseFeature) {
    this.sessionStorage.store('zest-cl-pause', clPauseFeature);
  }
  getClPause() {
    const jsClPaused = this.sessionStorage.retrieve('zest-cl-pause');
    return jsClPaused ? jsClPaused : null;
  }
  setKhoslaId(id) {
    this.sessionStorage.store('zest-khosla-id', id);
  }
  getKhoslaId() {
    return this.sessionStorage.retrieve('zest-khosla-id') ? this.sessionStorage.retrieve('zest-khosla-id') : false;
  }
  setLinkValidations(value: boolean) {
    this.sessionStorage.store('zest-validations', value);
  }
  getLinkValidations() {
    return this.sessionStorage.retrieve('zest-validations') ? this.sessionStorage.retrieve('zest-validations') : false;
  }
  // setMixpanelId(id: string) {
  //  this.sessionStorage.store('zest-mixpanel-id', id);
  // }
  // getMixpanelId() {
  //  return this.sessionStorage.retrieve('zest-mixpanel-id') ? this.sessionStorage.retrieve('zest-mixpanel-id') : false;
  // }
  setFlipkartUltra(val: boolean) {
    this.sessionStorage.store('zest-flipkart-ultra-flow', val);
  }
  getFlipkartUltraFlag() {
    return this.sessionStorage.retrieve('zest-flipkart-ultra-flow');
  }
  getClIncreaseLinkFlag() {
    return this.sessionStorage.retrieve('zest-cl-increase-link') && this.sessionStorage.retrieve('zest-cl-increase-link').link ? this.sessionStorage.retrieve('zest-cl-increase-link').link : false;
  }
  setClIncreaseLink(link = true, flow = 'link') {
    const val = new CliLinkData(link, flow);
    this.sessionStorage.store('zest-cl-increase-link', val);
  }
  setPerfiosLink(val = true) {
    this.sessionStorage.store('zest-perfios-link', val);
  }
  setVideoKycLink(val = true) {
    this.sessionStorage.store('zest-video-kyc-link', val);
  }
  getVideoKycLinkFlag() {
    return this.sessionStorage.retrieve('zest-video-kyc-link') ? this.sessionStorage.retrieve('zest-video-kyc-link') : false;
  }
  getPerfiosLinkFlag() {
    return this.sessionStorage.retrieve('zest-perfios-link') ? this.sessionStorage.retrieve('zest-perfios-link') : false;
  }
  setRestructuringLAFlag(flag = true) {
    this.sessionStorage.store('zest-restructuring-la', flag);
  }
  getRestructuringLAFlag() {
    return this.sessionStorage.retrieve('zest-restructuring-la') ? this.sessionStorage.retrieve('zest-restructuring-la') : false;
  }
  setRestructuredApplication(application: { applicationId: string, merchantId: string }) {
    this.sessionStorage.store('zest-restructured-application', application);
  }
  getRestructuredApplication() {
    return this.sessionStorage.retrieve('zest-restructured-application') ? this.sessionStorage.retrieve('zest-restructured-application') : false;
  }
  setCustomerContact(phoneNumber: string) {
    this.sessionStorage.store('zest-contact', phoneNumber);
  }
  getCustomerContact() {
    return this.sessionStorage.retrieve('zest-contact');
  }
  setBdPostKycFlow(value = true) {
    this.sessionStorage.store('zest-bd-post-kyc', value);
  }
  getBdPostKycFlow() {
    return this.sessionStorage.retrieve('zest-bd-post-kyc') ? this.sessionStorage.retrieve('zest-bd-post-kyc') : false;
  }
  setShowSanctionLetterPage(value: any) {
    this.sessionStorage.store('zest-sanction-letter-page', value);
  }
  getShowSanctionLetterPage() {
    return this.sessionStorage.retrieve('zest-sanction-letter-page') ? this.sessionStorage.retrieve('zest-sanction-letter-page') : {};
  }
  setCheckoutParams(value: any) {
    this.sessionStorage.store('zest-checkout-params', value);
  }
  getCheckoutParams() {
    return this.sessionStorage.retrieve('zest-checkout-params') ? this.sessionStorage.retrieve('zest-checkout-params') : null;
  }
  setReactivationContext() {
    this.sessionStorage.store('zest-reactivation-flow', true);
  }
  getReactivationContext() {
    return this.sessionStorage.retrieve('zest-reactivation-flow') ? this.sessionStorage.retrieve('zest-reactivation-flow') : false;
  }
  setUnauthorizedRedirection(value: any) {
    this.sessionStorage.store('zest-unauthorized-redirection', value);
  }
  getUnauthorizedRedirection() {
    return this.sessionStorage.retrieve('zest-unauthorized-redirection') ? this.sessionStorage.retrieve('zest-unauthorized-redirection') : null;
  }
  setJourneyFlowType(value = JourneyFlowType.Default) {
    this.sessionStorage.store('zest-flow-type', value);
  }
  getJourneyFlowType() {
    return this.sessionStorage.retrieve('zest-flow-type') ? this.sessionStorage.retrieve('zest-flow-type') : JourneyFlowType.Default;
  }
  setAPRContent(data: any) {
    this.sessionStorage.store('zest-apr-data', data);
  }
  getAPRContent() {
    return this.sessionStorage.retrieve('zest-apr-data') ? this.sessionStorage.retrieve('zest-apr-data') : null;
  }
}
